import { Box } from "../../box";
import { Card } from "../../card";
import { NumericValue } from "../../numeric-value";
import { Tooltip } from "../../tooltip";
import { checkHasTooltipContent } from "./metric-box.utilities";
import type { IProps } from "./metric-box.types";
import { FunctionComponent, memo, useState } from "react";

/** Default `renderValue` implementation, hoisted to module scope so it's a stable reference across renders. */
const defaultRenderValue: NonNullable<IProps["renderValue"]> = (value, otherProps) => (
    <NumericValue value={value} {...otherProps} />
);

/**
 * Display a metric value optionally versus a comparison value.
 * @param props.value           Value for the metric.
 * @param props.icon            Icon for the metric.
 * @param props.isLoading       If the data is loading.
 * @param props.menuOptions     Menu actions for metrics.
 * @param props.renderValue     Custom renderer for the value and comparison value. Defaults to rendering via
 *                              NumericValue.
 * @param props.title           Label/description for the metric.
 * @param props.tooltipContent  Explanation of the metric, shown in a tooltip while the card is hovered. Stays closed
 *                              while the pointer is over a custom `footer`, so that footer can own its own tooltip.
 * @param props.comparisonValue Value shown in the footer as a comparison against `value`. Ignored when `footer` is
 *                              provided.
 * @param props.footer          Custom footer content (e.g. a coloured comparison badge) that replaces the default
 *                              "vs {value} overall" text when provided.
 * @param props.color           Card accent color.
 * @param props["data-testid"]  Test id applied to the underlying Card.Metric.
 * @param props.options         Intl.NumberFormat options passed through to the value renderer.
 * @param props.unit            Unit suffix appended to the rendered value (e.g. "%").
 * @returns                     A metric icon, label, and values.
 */
const MetricBoxComponent: FunctionComponent<IProps> = ({
    comparisonValue,
    footer,
    icon,
    isLoading,
    menuOptions,
    renderValue = defaultRenderValue,
    title,
    tooltipContent,
    value,
    color,
    "data-testid": dataTestId,
    ...numericValueProps
}) => {
    const [isCardHovered, setIsCardHovered] = useState(false);
    const [isFooterHovered, setIsFooterHovered] = useState(false);

    if (isLoading) {
        return <Card.Metric.Skeleton />;
    }

    // Computed only when actually rendered, so a custom renderValue is never invoked for the loading skeleton or
    // when a custom footer replaces the default comparison footer.
    const buildDefaultFooter = () =>
        typeof comparisonValue === "undefined" || comparisonValue === null ? undefined : (
            <>vs {renderValue(comparisonValue, numericValueProps)} overall</>
        );

    // Card.Metric has no tooltip of its own, so the explanation is attached by wrapping the whole card.
    const hasTooltip = checkHasTooltipContent(tooltipContent);

    // Caller-provided footers often carry their own tooltip (e.g. a comparison badge), so hovering one has to keep the
    // card's tooltip closed rather than opening both at once. The default "vs ... overall" footer owns no tooltip, so it
    // is left to hover like the rest of the card. Inline `span` because Card.Metric renders the footer inside a <p>.
    const buildFooter = () => {
        if (typeof footer === "undefined") {
            return buildDefaultFooter();
        }

        if (!hasTooltip) {
            return footer;
        }

        return (
            <Box
                component="span"
                onMouseEnter={() => setIsFooterHovered(true)}
                onMouseLeave={() => setIsFooterHovered(false)}
            >
                {footer}
            </Box>
        );
    };

    // `opened` is controlled so the footer can suppress it; Mantine's own hover handling cannot be told to stand aside.
    // Leaving the card also clears the footer flag, so a footer that unmounts while hovered can't leave it stuck closed.
    // `disabled` covers the nothing-to-explain case, which would otherwise open an empty bubble on hover.
    return (
        <Tooltip
            label={tooltipContent}
            disabled={!hasTooltip}
            opened={isCardHovered && !isFooterHovered}
            onMouseEnter={() => setIsCardHovered(true)}
            onMouseLeave={() => {
                setIsCardHovered(false);
                setIsFooterHovered(false);
            }}
        >
            <Card.Metric
                title={title}
                icon={icon}
                color={color}
                menu={menuOptions}
                metric={[
                    {
                        value: typeof value === "number" ? renderValue(value, numericValueProps) : "--",
                        footer: buildFooter(),
                    },
                ]}
                data-testid={dataTestId}
            />
        </Tooltip>
    );
};

const MetricBox = memo(MetricBoxComponent);

export { MetricBox };
